import type { ReactNode } from 'react';
import { Breadcrumbs, BreadcrumbsItem } from '@heroui/react';
import { Link } from 'react-router-dom';
import { cn } from '../utils';
import FavoriteButton from '../components/FavoriteButton';
import { FavoriteTarget, UserFavorite } from 'src/api/types/favorite';

export interface PageHeaderBreadcrumb {
  label: string;
  to?: string;
}

export interface PageHeaderProps {
  title: ReactNode;
  eyebrow?: ReactNode;
  description?: ReactNode;
  breadcrumbs?: PageHeaderBreadcrumb[];
  actions?: ReactNode;
  children?: ReactNode;
  className?: string;
  favoriteTarget?: FavoriteTarget;
  favoriteLabel?: string;
  favoritePath?: string;
  favoriteMetadata?: UserFavorite['target'];
}

export const PageHeader = ({
  title,
  eyebrow,
  description,
  breadcrumbs,
  actions,
  children,
  className,
  favoriteTarget,
  favoriteLabel,
  favoritePath,
  favoriteMetadata
}: PageHeaderProps) => {
  const hasBreadcrumbs = (breadcrumbs?.length ?? 0) > 0;

  return (
    <header className={cn('mb-6', className)}>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1 space-y-3">
          {hasBreadcrumbs || favoriteTarget ? (
            <div className="flex min-w-0 items-center">
              {favoriteTarget ? (
                <FavoriteButton
                  target={favoriteTarget}
                  label={favoriteLabel ?? (typeof title === 'string' ? title : undefined)}
                  path={favoritePath}
                  targetMetadata={favoriteMetadata}
                />
              ) : null}
              {hasBreadcrumbs ? (
                <Breadcrumbs>
                  {breadcrumbs?.map((item) => (
                    <BreadcrumbsItem key={`${item.to ?? 'current'}-${item.label}`}>
                      {item.to ? <Link to={item.to}>{item.label}</Link> : item.label}
                    </BreadcrumbsItem>
                  ))}
                </Breadcrumbs>
              ) : null}
            </div>
          ) : null}
          <div className="space-y-2">
            {eyebrow ? (
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
                {eyebrow}
              </p>
            ) : null}
            <h1 className="min-w-0 truncate text-2xl font-semibold tracking-tight text-foreground">
              {title}
            </h1>
            {description ? (
              <p className="text-sm leading-6 text-default-500">{description}</p>
            ) : null}
          </div>
          {children}
        </div>
        {actions ? (
          <div className="flex w-full flex-wrap justify-start gap-2 sm:w-auto sm:justify-end">
            {actions}
          </div>
        ) : null}
      </div>
    </header>
  );
};
